import React, { useEffect, useState } from "react";
import { Plus, Search, Building2, X } from "lucide-react";
import Loader from "@/components/Loader";
import DialogBox from "@/components/Common/DialogBox";
import useToastStore from "@/components/Common/Toast/toastStore";
import TemplateCard from "@/components/NoteTemplates/TemplateCard";
import ToggleSwitch from "@/components/ui/ToggleSwitch";
import Dropdown from "@/components/ui/Dropdown";
import Pagination from "@/components/Common/Pagination";
import { noteTemplateService } from "@/services/noteTemplateService";
import { companiesService } from "@/services/companiesService";
import { recruitersService } from "@/services/recruitersService";
import useAuth from "@/hooks/stores/useAuth";
import { useDebounce } from "@/utils/helpers";

const mapCompaniesToSelected = (companies = []) =>
  companies.map((company) => ({
    value: company.id,
    label: company.name ?? company.title ?? "",
  }));

const getTemplateOwnerId = (item) =>
  item?.user_id ??
  item?.created_by?.id ??
  item?.owner_id ??
  item?.created_by_id ??
  null;

const NoteTemplates = () => {
  const { showToast } = useToastStore;
  const { userRole, userID } = useAuth();
  const rawData = useAuth((state) => state.getRawData());
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 24,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null,
  });
  const [formData, setFormData] = useState({
    name: "",
    body: "",
    is_private: true,
  });
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [pagination.current_page, pagination.per_page, searchTerm]);

  useEffect(() => {
    if (!showFormModal) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const role = userRole?.toLowerCase();
        let aggregated = [];

        if (role === "recruiter") {
          let currentPage = 1;
          let lastPage = 1;
          do {
            const response = await recruitersService.getCompanyOfRecruiter({
              per_page: 100,
              page: currentPage,
            });
            const pageData = Array.isArray(response?.data) ? response.data : [];
            aggregated = aggregated.concat(pageData);
            lastPage = response?.pagination?.last_page ?? 1;
            currentPage += 1;
          } while (currentPage <= lastPage);
        } else if (role === "admin" || role === "company_admin") {
          let currentPage = 1;
          let lastPage = 1;
          do {
            const response = await companiesService.getCompaniesList({
              per_page: 100,
              page: currentPage,
            });
            const pageData = Array.isArray(response?.companies)
              ? response.companies
              : [];
            aggregated = aggregated.concat(pageData);
            lastPage = response?.pagination?.last_page ?? 1;
            currentPage += 1;
          } while (currentPage <= lastPage);
        } else if (role === "company") {
          const companyId = rawData?.user?.company_logins?.[0]?.company_id;
          const companyName =
            rawData?.user?.company_logins?.[0]?.company?.name ??
            rawData?.user?.company_logins?.[0]?.company_name;
          if (companyId) {
            aggregated = [{ id: companyId, name: companyName ?? "Company" }];
          }
        }

        setCompanies(aggregated);
      } catch (error) {
        showToast({
          message: error.message || "Failed to load companies",
          type: "error",
        });
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [showFormModal, userRole, rawData]);

  useEffect(() => {
    if (
      !showFormModal ||
      formData.is_private ||
      companies.length === 0 ||
      selectedCompanies.length === 0
    ) {
      return;
    }

    setSelectedCompanies((prev) =>
      prev.map((selected) => {
        const match = companies.find((c) => c.id === selected.value);
        return match
          ? { value: match.id, label: match.name }
          : selected;
      }),
    );
  }, [companies, showFormModal, formData.is_private]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        search: searchTerm,
      };
      const response = await noteTemplateService.getAllTemplates(params);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.templates)
          ? response.templates
        : Array.isArray(response)
          ? response
          : [];

      setTemplates(
        list.map((item) => {
          const isPrivate = Boolean(item.is_private ?? item.private_note);
          const ownerId = getTemplateOwnerId(item);
          const isTemplateOwner = ownerId
            ? String(ownerId) === String(userID)
            : false;
          return {
            id: item.id,
            name: item.name ?? item.title ?? "",
            body: item.body ?? item.content ?? "",
            is_private: isPrivate,
            isShared: !isPrivate,
            isOwner: isTemplateOwner,
            canModify:
              item.can_modify !== undefined
                ? Boolean(item.can_modify)
                : item.can_edit !== undefined
                  ? Boolean(item.can_edit)
                  : isTemplateOwner,
            owner_id: ownerId,
            companies: item.companies ?? [],
            company_ids:
              item.company_ids ?? item.companies?.map((c) => c.id) ?? [],
          };
        }),
      );

      if (response?.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      showToast({
        message: error.message || "Failed to fetch templates",
        type: "error",
      });
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  const resetForm = () => {
    setSelectedTemplate(null);
    setSelectedCompanies([]);
    setFormData({
      name: "",
      body: "",
      is_private: true,
    });
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowFormModal(true);
  };

  const handleOpenEdit = (template) => {
    if (!template.canModify) {
      showToast({
        message: "You do not have permission to edit this template",
        type: "error",
      });
      return;
    }
    setSelectedTemplate(template);
    const isPrivate = Boolean(template.is_private);
    setFormData({
      name: template.name || "",
      body: template.body || "",
      is_private: isPrivate,
    });
    if (!isPrivate && template.companies?.length) {
      setSelectedCompanies(mapCompaniesToSelected(template.companies));
    } else if (!isPrivate && template.company_ids?.length) {
      setSelectedCompanies(
        template.company_ids.map((id) => {
          const company = template.companies?.find((c) => c.id === id);
          return {
            value: id,
            label: company?.name ?? `Company ${id}`,
          };
        }),
      );
    } else {
      setSelectedCompanies([]);
    }
    setShowFormModal(true);
  };

  const handleOpenView = (template) => {
    setViewTemplate(template);
    setShowViewModal(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      showToast({
        message: "Template name is required",
        type: "error",
      });
      return;
    }

    if (!formData.is_private && selectedCompanies.length === 0) {
      showToast({
        message: "Select at least one company to share this template",
        type: "error",
      });
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        name: formData.name,
        body: formData.body,
        is_private: formData.is_private,
        company_ids: formData.is_private
          ? []
          : selectedCompanies.map((company) => company.value),
      };

      if (selectedTemplate?.id) {
        await noteTemplateService.updateTemplate(selectedTemplate.id, payload);
        showToast({
          message: "Template updated successfully!",
          type: "success",
        });
      } else {
        await noteTemplateService.createTemplate(payload);
        showToast({
          message: "Template created successfully!",
          type: "success",
        });
      }

      setShowFormModal(false);
      resetForm();
      await loadTemplates();
    } catch (error) {
      showToast({
        message: error.message || "Failed to save template",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (template) => {
    if (!template.canModify) {
      showToast({
        message: "You do not have permission to delete this template",
        type: "error",
      });
      return;
    }
    setTemplateToDelete(template.id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      setIsDeleting(true);
      await noteTemplateService.deleteTemplate(templateToDelete);
      showToast({
        message: "Template deleted successfully!",
        type: "success",
      });
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      await loadTemplates();
    } catch (error) {
      showToast({
        message: error.message || "Failed to delete template",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading templates..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Note Templates
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage note templates and external mappings for consistent notes
                across your team.
              </p>
            </div>
            <button
              onClick={handleOpenAdd}
              className="bg-primary-dark text-white px-4 h-[36px] rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Note Template
            </button>
          </div>
{/* //search bar */}
          <div className="w-full md:w-[384px]">
            <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search"
                value={tempSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
              />
            </div>
          </div>
        </div>

        {templates.length === 0 ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)]">
            <h3 className="text-lg font-medium text-text-foreground">
              No Templates Found
            </h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleOpenEdit}
                onDelete={handleDeleteClick}
                onView={handleOpenView}
              />
            ))}
          </div>
        )}
      </div>
      <div className="p-6">
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.last_page}
          onPageChange={handlePageChange}
          per_page={pagination.per_page}
        />
      </div>

      <DialogBox
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          resetForm();
        }}
        title={selectedTemplate ? "Edit Note Template" : "Add Note Template"}
        className="w-[40%] max-md:w-[60%]"
        showIcon={false}
        actionButton={
          <button
            onClick={handleSave}
            className={`bg-primary-dark text-white px-5 py-2 rounded-lg hover:bg-primary-dark transition-colors ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={isSaving}
          >
            {selectedTemplate ? "Update Template" : "Save Template"}
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Template Name
            </label>
            <input
              type="text"
              placeholder="Enter template name"
              className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full outline-none focus:ring-1 focus:ring-ring"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Body
            </label>
            <textarea
              placeholder="Enter template body"
              className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full min-h-[120px] outline-none focus:ring-1 focus:ring-ring"
              value={formData.body}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  body: e.target.value,
                }))
              }
            />
          </div>
          <ToggleSwitch
            label="Private Note"
            checked={formData.is_private}
            onChange={() => {
              const nextIsPrivate = !formData.is_private;
              setFormData((prev) => ({
                ...prev,
                is_private: nextIsPrivate,
              }));
              if (nextIsPrivate) {
                setSelectedCompanies([]);
              }
            }}
          />
          {!formData.is_private && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Share with companies
              </label>
              <Dropdown
                options={companies.map((company) => ({
                  label: company.name,
                  value: company.id,
                }))}
                selectedValues={selectedCompanies}
                onChange={setSelectedCompanies}
                placeholder="Select companies..."
                searchable
                multiSelect
                isLoading={loadingCompanies}
                loadingText="Loading Companies..."
                icon={
                  <Building2 className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                }
                className="w-full"
                triggerClassName="bg-card border border-input"
              />
              {selectedCompanies.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedCompanies.map((company) => (
                    <div
                      key={company.value}
                      className="flex items-center bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium border border-border"
                    >
                      <Building2 className="w-4 h-4 inline-block mr-2 text-muted-foreground" />
                      {company.label}
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedCompanies((prev) =>
                            prev.filter((c) => c.value !== company.value),
                          )
                        }
                        className="ml-2 text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogBox>

      <DialogBox
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewTemplate(null);
        }}
        title="Template Details"
        className="w-[45%] max-md:w-[70%]"
        showIcon={false}
      >
        <div className="">
          <div className="  py-3">
          
            <p className="text-lg font-semibold text-primary-dark break-words leading-7">
              {viewTemplate?.name || "-"}
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card px-4 py-4">
           
            <div className="max-h-[45vh] overflow-y-auto pr-1">
              <p className="text-base text-muted-foreground whitespace-pre-wrap break-words leading-8">
                {viewTemplate?.body || "-"}
              </p>
            </div>
          </div>
        </div>
      </DialogBox>

      <DialogBox
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTemplateToDelete(null);
        }}
        title="Delete Note Template"
        className="w-[40%] max-md:w-[60%]"
        actionButton={
          <button
            onClick={handleDelete}
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
            disabled={isDeleting}
          >
            Delete
          </button>
        }
      >
        <p className="text-base text-muted-foreground">
          Are you sure you want to delete this template? This action cannot be
          undone.
        </p>
      </DialogBox>
    </div>
  );
};

export default NoteTemplates;
